import React, { useState } from "react";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";

const NAVY = "#0d1f3c";

const DARK = {
  bg: "#0d1f3c", surface: "#142a4d", surface2: "#1a3358",
  border: "rgba(255,255,255,0.10)",
  accent: "#3b82f6", accentL: "#60a5fa", accentBg: "rgba(59,130,246,0.12)",
  green: "#34d399", greenBg: "rgba(52,211,153,0.10)",
  text: "#f0f4f8", textMid: "rgba(240,244,248,0.78)", textSoft: "rgba(240,244,248,0.50)",
};

const LIGHT = {
  bg: "#f8fafc", surface: "#ffffff", surface2: "#f1f5f9",
  border: "#e2e8f0",
  accent: "#2563eb", accentL: "#3b82f6", accentBg: "rgba(37,99,235,0.08)",
  green: "#16a34a", greenBg: "rgba(22,163,74,0.08)",
  text: "#0f172a", textMid: "#475569", textSoft: "#94a3b8",
};

const BENEFITS = [
  { icon: "📣", title: "Priority Visibility",     desc: "Your loads and jobs surface higher for verified drivers actively searching in your lanes." },
  { icon: "🛡️", title: "Verified Network Access", desc: "Book only ID-checked, certification-verified escort drivers — no cold-calling Facebook groups." },
  { icon: "🤝", title: "Co-Marketing",             desc: "Joint case studies, logo placement on our site, and cross-promotion to our driver and company base." },
  { icon: "💳", title: "Preferred Rates",          desc: "Lower platform fees on invoicing and job posting once you hit partner tier." },
  { icon: "🎧", title: "Dedicated Support",        desc: "A named account contact instead of a support queue — for onboarding, disputes, or scaling up." },
  { icon: "📊", title: "Reporting & Insights",     desc: "Lane-level demand data and driver-availability trends to help you plan ahead." },
];

const STEPS = [
  { n: "1", title: "Apply",   desc: "Tell us about your company — fleet size, lanes you run, and what you're looking for." },
  { n: "2", title: "Verify",  desc: "We confirm your business details, insurance, and operating history — usually within 2 business days." },
  { n: "3", title: "Go Live", desc: "Your logo goes up, your account manager reaches out, and you start booking from our verified network." },
];

export default function BecomePartnerPage({ isDarkMode, toggleTheme }) {
  const T = isDarkMode ? DARK : LIGHT;
  const [form, setForm] = useState({ company: "", contact: "", email: "", phone: "", website: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoError, setLogoError] = useState("");

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setLogoError("Please upload an image file (PNG, JPG, or SVG).");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setLogoError("Please keep the logo under 3MB.");
      return;
    }
    setLogoError("");
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.company.trim() || !form.contact.trim() || !form.email.trim()) {
      setError("Company, contact name, and email are required.");
      return;
    }
    setError("");

    // TODO: wire this up to your real endpoint, e.g.:
    // const body = new FormData();
    // Object.entries(form).forEach(([k, v]) => body.append(k, v));
    // if (logoFile) body.append("logo", logoFile);
    // await fetch("/api/partner-inquiries", { method: "POST", body });
    // For now this just confirms the submission in the UI.
    setSubmitted(true);
  };

  const inputStyle = {
    width: "100%", boxSizing: "border-box",
    padding: "12px 14px", borderRadius: 10,
    border: `1px solid ${T.border}`, background: T.surface2,
    color: T.text, fontSize: 14, fontFamily: "inherit", outline: "none",
  };
  const labelStyle = { fontSize: 12.5, fontWeight: 700, color: T.textMid, marginBottom: 6, display: "block" };

  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: "'Inter',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@700;800;900&display=swap');
        *{box-sizing:border-box;}
        .pp-input:focus { border-color: ${T.accent} !important; }
      `}</style>

      <Navbar isDarkMode={isDarkMode} toggleTheme={toggleTheme} />

      {/* Hero */}
      <section style={{
        background: `linear-gradient(160deg, ${NAVY} 0%, #1a3358 60%, ${NAVY} 100%)`,
        padding: "clamp(48px, 8vw, 84px) clamp(20px, 4vw, 40px)", textAlign: "center",
      }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "rgba(59,130,246,0.14)", border: "1px solid rgba(96,165,250,0.35)",
            borderRadius: 99, padding: "5px 14px", marginBottom: 18,
            fontSize: 12, fontWeight: 700, color: "#93c5fd",
          }}>
            Carrier & Logistics Partnerships
          </div>
          <h1 style={{
            fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(30px, 5vw, 46px)",
            fontWeight: 900, color: "#fff", lineHeight: 1.15, marginBottom: 14,
          }}>
            Partner With PilotLink USA
          </h1>
          <p style={{ color: "rgba(240,244,248,0.72)", fontSize: "clamp(15px, 2vw, 17px)", lineHeight: 1.7 }}>
            Get priority access to our verified escort driver network, co-marketing support, and a dedicated account manager for your team.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section style={{ padding: "clamp(44px, 7vw, 72px) clamp(20px, 4vw, 40px)" }}>
        <div style={{ maxWidth: 1140, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "clamp(28px, 5vw, 40px)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", color: isDarkMode ? T.accentL : T.accent, marginBottom: 8 }}>
              Why Partner
            </div>
            <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(24px, 4vw, 32px)", fontWeight: 800, color: T.text }}>
              Built for Carriers Who Move Volume
            </h2>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap: "clamp(14px, 2.5vw, 20px)" }}>
            {BENEFITS.map((b) => (
              <div key={b.title} style={{
                background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
                padding: "clamp(18px, 2.5vw, 24px)",
              }}>
                <div style={{
                  width: 46, height: 46, borderRadius: 12, background: T.accentBg,
                  border: `1px solid ${T.accent}33`, display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: 22, marginBottom: 14,
                }}>{b.icon}</div>
                <div style={{ fontWeight: 700, fontSize: 16, color: T.text, marginBottom: 6 }}>{b.title}</div>
                <div style={{ fontSize: 13.5, color: T.textMid, lineHeight: 1.6 }}>{b.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section style={{ background: isDarkMode ? T.surface : T.surface2, borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`, padding: "clamp(44px, 7vw, 72px) clamp(20px, 4vw, 40px)" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(22px, 3.5vw, 28px)", fontWeight: 800, color: T.text, textAlign: "center", marginBottom: "clamp(28px, 5vw, 40px)" }}>
            How to Become a Partner
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: "clamp(16px, 3vw, 24px)" }}>
            {STEPS.map((s) => (
              <div key={s.n} style={{ textAlign: "center" }}>
                <div style={{
                  width: 44, height: 44, borderRadius: "50%", background: T.accent, color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 800, fontSize: 17, margin: "0 auto 14px",
                }}>{s.n}</div>
                <div style={{ fontWeight: 700, fontSize: 15.5, color: T.text, marginBottom: 6 }}>{s.title}</div>
                <div style={{ fontSize: 13.5, color: T.textMid, lineHeight: 1.6, maxWidth: 260, margin: "0 auto" }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / application form */}
      <section style={{ padding: "clamp(44px, 7vw, 72px) clamp(20px, 4vw, 40px)" }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "clamp(24px, 4vw, 32px)" }}>
            <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(22px, 3.5vw, 28px)", fontWeight: 800, color: T.text, marginBottom: 8 }}>
              Tell Us About Your Company
            </h2>
            <p style={{ fontSize: 14, color: T.textSoft }}>We typically respond within 2 business days.</p>
          </div>

          {submitted ? (
            <div style={{
              background: T.greenBg, border: `1px solid ${T.green}44`, borderRadius: 14,
              padding: "clamp(24px, 4vw, 32px)", textAlign: "center",
            }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>✅</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: T.text, marginBottom: 6 }}>Thanks — we've got it.</div>
              <div style={{ fontSize: 13.5, color: T.textMid }}>Our partnerships team will reach out at {form.email} shortly.</div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{
              background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16,
              padding: "clamp(20px, 3vw, 30px)", display: "flex", flexDirection: "column", gap: 16,
            }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))", gap: 16 }}>
                <div>
                  <label style={labelStyle}>Company Name *</label>
                  <input className="pp-input" style={inputStyle} value={form.company} onChange={update("company")} placeholder="Acme Freight Co." />
                </div>
                <div>
                  <label style={labelStyle}>Contact Name *</label>
                  <input className="pp-input" style={inputStyle} value={form.contact} onChange={update("contact")} placeholder="Jane Smith" />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))", gap: 16 }}>
                <div>
                  <label style={labelStyle}>Email *</label>
                  <input className="pp-input" type="email" style={inputStyle} value={form.email} onChange={update("email")} placeholder="jane@acmefreight.com" />
                </div>
                <div>
                  <label style={labelStyle}>Phone</label>
                  <input className="pp-input" style={inputStyle} value={form.phone} onChange={update("phone")} placeholder="(555) 123-4567" />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Company Website</label>
                <input className="pp-input" style={inputStyle} value={form.website} onChange={update("website")} placeholder="https://" />
              </div>
              <div>
                <label style={labelStyle}>Tell us about your lanes & volume</label>
                <textarea className="pp-input" style={{ ...inputStyle, minHeight: 100, resize: "vertical" }} value={form.message} onChange={update("message")} placeholder="e.g. 40+ oversize loads/month across TX, OK, LA..." />
              </div>

              {/* Logo upload — instant preview of how it'll sit in our partner strip */}
              <div>
                <label style={labelStyle}>Your Logo (optional)</label>
                <div style={{ fontSize: 12.5, color: T.textSoft, marginBottom: 8, lineHeight: 1.5 }}>
                  Upload it here and we'll show you exactly how it'll look next to our other partners on the site — no need to wait for us to mock it up.
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  style={{ fontSize: 13, color: T.textMid }}
                />
                {logoError && <div style={{ fontSize: 12.5, fontWeight: 600, color: "#dc2626", marginTop: 6 }}>{logoError}</div>}

                {logoPreview && (
                  <div style={{
                    marginTop: 14, background: isDarkMode ? T.surface2 : "#eff6ff",
                    border: `1px dashed ${T.accent}66`, borderRadius: 12,
                    padding: "clamp(14px, 2.5vw, 18px)",
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: isDarkMode ? T.accentL : T.accent, marginBottom: 10 }}>
                      Preview — how you'll appear on our site
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 140px), 1fr))", gap: 10 }}>
                      <div style={{
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6,
                        background: T.surface, border: `2px solid ${T.accent}`, borderRadius: 12,
                        padding: "12px 10px", textAlign: "center",
                      }}>
                        <img src={logoPreview} alt="Your logo preview" style={{ maxHeight: 26, maxWidth: 100, objectFit: "contain" }} />
                        <span style={{ fontSize: 10, fontWeight: 800, color: isDarkMode ? T.accentL : T.accent, letterSpacing: ".03em" }}>{form.company || "YOUR COMPANY"}</span>
                      </div>
                      {/* A couple of existing-style chips alongside, purely for context */}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "12px 10px", opacity: 0.6, filter: "grayscale(1)" }}>
                        <span style={{ fontSize: 16 }}>🛡️</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: T.textMid }}>Highway Shield Insurance</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "12px 10px", opacity: 0.6, filter: "grayscale(1)" }}>
                        <span style={{ fontSize: 16 }}>🛣️</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: T.textMid }}>Frontier Carriers</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {error && (
                <div style={{ fontSize: 13, fontWeight: 600, color: "#dc2626" }}>{error}</div>
              )}

              <button type="submit" style={{
                background: T.accent, color: "#fff", border: "none", borderRadius: 10,
                padding: "14px", fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: "inherit",
                boxShadow: `0 4px 14px ${T.accentBg}`,
              }}>
                Submit Partner Inquiry →
              </button>
              <div style={{ fontSize: 12, color: T.textSoft, textAlign: "center" }}>
                Prefer email? Reach us directly at <strong style={{ color: T.textMid }}>partners@pilotlinkusa.com</strong>
              </div>
            </form>
          )}
        </div>
      </section>

      <Footer isDarkMode={isDarkMode} />
    </div>
  );
}
